import React, { createContext, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDates } from '../../../hooks/useDates'
import { useOperationalTable } from '../OperationalTable'
import { useVariables } from '../../../providers/VariableProvider'
import { useAppContext } from '../../../redux/slices/appContext'
import TransactionsColumnProvider from './TransactionsColumnContext'
import { useClientDateRange, useTransactionsCounts, useTransactionsData } from './useTransactionsData'
import { useSubscribedFilters } from './useSubscribedFilters'

const TransactionsContext = createContext({})

export const useTransactionsContext = () => useContext(TransactionsContext)

function TransactionsProvider ({
  defaultDateRange,
  storageKey,
  scope,
  defaultFilter,
  defaultSort,
  columns,
  dateRangeVariable,
  asOfDateVariable,
  customStartVariable,
  customEndVariable,
  children
}) {
  const { getVariable } = useVariables()
  const [featuredFilters, setFeaturedFilters] = useState([])
  const [textFilter, setTextFilter] = useState({})
  const [tableFilter, setTableFilter] = useState({})
  const { availableDates } = useDates()
  const {
    sort,
    onSortingChange,
    pageIndex,
    pageSize,
    onPagingChange
  } = useOperationalTable({ defaultSort })

  const levelFilters = useSubscribedFilters()
  const { clientId } = useAppContext()
  const variableDateRange = getVariable(dateRangeVariable, defaultDateRange)
  const variableAsOfDate = getVariable(asOfDateVariable, availableDates.main)
  const {
    value: clientDates,
    loading: clientDatesLoading
  } = useClientDateRange(
    variableDateRange === 'ctm' || !variableDateRange ? null : clientId,
    variableAsOfDate,
    variableDateRange
  )

  const useTransactionDates = useMemo(() => {
    if (variableDateRange === 'ctm') {
      return {
        value: { startDate: getVariable(customStartVariable), endDate: getVariable(customEndVariable) },
        loading: false
      }
    }

    return () => ({
      value: clientDates,
      loading: clientDatesLoading
    })
  }, [variableDateRange, getVariable, customStartVariable, customEndVariable, clientDates, clientDatesLoading])

  const {
    transactions,
    transactionsLoading
  } = useTransactionsData({
    featuredFilters,
    levelFilters,
    textFilter,
    tableFilter,
    scope,
    defaultFilter,
    sort,
    pageSize,
    pageIndex,
    useTransactionDates
  })

  const {
    counts,
    countsLoading
  } = useTransactionsCounts({
    featuredFilters,
    levelFilters,
    textFilter,
    tableFilter,
    scope,
    defaultFilter,
    useTransactionDates
  })

  const value = useMemo(() => ({
    dateRange: variableDateRange,
    // setDateRange,
    asOfDate: availableDates.mainDate,

    featuredFilters,
    setFeaturedFilter: (f) => setFeaturedFilters([f]),
    addFeaturedFilter: f => setFeaturedFilters(prev => {
      if (prev.some(x => x.id === f.id)) return prev
      return [...prev, f]
    }),
    removeFeaturedFilter: f => setFeaturedFilters(prev => {
      if (!prev.some(x => x.id === f.id)) return prev
      return prev.filter(x => x.id !== f.id)
    }),
    clearFeaturedFilters: () => setFeaturedFilters([]),

    textFilter,
    setTextFilter,

    tableFilter,
    setTableFilter,

    transactions,
    transactionsLoading: transactionsLoading,
    counts,
    countsLoading,
    defaultFilter,
    scope,

    onPagingChange,
    onSortingChange
  }), [
    variableDateRange,
    featuredFilters, setFeaturedFilters,
    textFilter, setTextFilter,
    tableFilter, setTableFilter,
    availableDates.mainDate,
    transactions, transactionsLoading,
    counts, countsLoading,
    defaultFilter,
    onPagingChange,
    onSortingChange,
    scope
  ])

  return (
    <TransactionsContext.Provider value={value}>
      <TransactionsColumnProvider storageKey={storageKey} columns={columns} defaultSort={defaultSort}>
        {children}
      </TransactionsColumnProvider>
    </TransactionsContext.Provider>
  )
}

TransactionsProvider.propTypes = {
  scope: PropTypes.oneOf(['client', 'firm']),
  columns: PropTypes.array,
  defaultSort: PropTypes.array,
  defaultFilter: PropTypes.object,
  defaultDateRange: PropTypes.string,
  storageKey: PropTypes.string,
  dateRangeVariable: PropTypes.string,
  asOfDateVariable: PropTypes.string,
  customStartVariable: PropTypes.string,
  customEndVariable: PropTypes.string,
  children: PropTypes.node
}

TransactionsProvider.defaultProps = {
  scope: 'client',
  defaultDateRange: 'YTD',
  dateRangeVariable: '@.filters.dateRange',
  asOfDateVariable: '@.filters.asOfDate'
}

export default TransactionsProvider
